// stats.ts — the M3 RETENTION/META layer: streaks, lifetime stats, per-topic bests, daily streak.
//
// This is PRESENTATION/meta only — it NEVER touches Core, the Sim, or determinism. It is a thin,
// best-effort wrapper over localStorage (tolerant of private-mode / blocked storage, exactly like
// settings.ts and the recents reader). Everything here is derived from a board's final Snapshot
// outcome that the renderer already has; Core stays frozen and integer-deterministic.
//
// Two cohesive ideas:
//   1) Lifetime stats  — games played, wins, win-rate, best score, best combo, per-topic best score.
//   2) Streaks         — a *win* streak (consecutive board wins) AND a *daily* streak (consecutive
//      UTC days the player completed the Daily). The daily streak is the retention hook: miss a day
//      and it resets; play today after yesterday and it climbs.
//
// All times use UTC day keys so a streak is stable across timezones/instances (mirrors the backend's
// utcDayKey in server.ts). We read Date ONLY here (renderer layer) — never in Core.

const KEY = 'trivia.stats.v1';

/** A single topic's personal best (keyed by the normalized topic slug). */
export interface TopicBest {
  /** human label for display (the topicRaw we last saw for this slug) */
  label: string;
  bestScore: number;
  /** number of times this topic was played to completion */
  plays: number;
  wins: number;
}

export interface Stats {
  /** total boards completed (won or lost) */
  gamesPlayed: number;
  /** total boards won */
  wins: number;
  /** highest single-board human score ever */
  bestScore: number;
  /** highest combo ever reached on any board */
  bestCombo: number;
  /** lifetime Gauntlets threaded (accumulated per board) — the signature-verb mastery signal */
  gauntletsThreaded: number;
  /** current consecutive-win streak (resets on any loss) */
  winStreak: number;
  /** all-time best win streak */
  bestWinStreak: number;
  /** consecutive UTC days the Daily was completed */
  dailyStreak: number;
  /** best-ever daily streak */
  bestDailyStreak: number;
  /** UTC day key (YYYY-MM-DD) the Daily was last completed, or '' if never */
  lastDailyDate: string;
  /**
   * STREAK FREEZE (retention safety net, R4): a bank of free "skip a day" tokens. If the player
   * misses exactly ONE UTC day, a token is spent to PROTECT the daily streak (it extends instead of
   * resetting to 1). Tokens are earned slowly (one per FREEZE_EARN_EVERY kept days) and capped at
   * FREEZE_CAP. Purely on the RESET-on-gap path — the extend/start/repeat semantics are untouched.
   */
  streakFreeze: number;
  /** kept-days counter toward the next earned freeze (rolls over at FREEZE_EARN_EVERY). */
  freezeProgress: number;
  /** per-topic personal bests, keyed by normalized slug */
  topics: Record<string, TopicBest>;
  /** PROGRESSION: lifetime XP (earned from every board's human score + win bonus). */
  xp: number;
  /** First-session achievement flags (each fires its juicy reward exactly once, ever). */
  firsts: Firsts;
  /** Today's daily-mission ledger (re-rolled on a new UTC day, deterministic per day). */
  daily: DailyMissionState;
}

/** Day-1 / first-session goals — each becomes true forever the first time the player does it. */
export interface Firsts {
  /** cleared their first board (the guaranteed completable session-1 goal) */
  firstWin: boolean;
  /** threaded their first Gauntlet (the signature-verb session-1 goal) */
  firstThread: boolean;
}

/** Per-mission progress for today. `cur` is clamped to `target` once `done`. */
export interface MissionProgress {
  id: MissionId;
  cur: number;
  done: boolean;
  /** true only on the board that just tipped it over (so Results can celebrate once) */
  justCompleted?: boolean;
}

/** Today's mission set + the UTC day it was rolled for (drives the once-a-day reset). */
export interface DailyMissionState {
  /** the UTC day key these missions belong to; '' before any roll */
  day: string;
  progress: MissionProgress[];
}

/** What the renderer hands us after a board ends — already in hand from the Snapshot. */
export interface BoardOutcome {
  topicNormalized: string;
  topicLabel: string;
  humanScore: number;
  /** peak combo reached this board (renderer tracks it from the snapshot) */
  bestCombo: number;
  won: boolean;
  /** true when this board was launched from the Daily button */
  isDaily: boolean;
  /** Gauntlets threaded this board (derived by the renderer from the snapshot/run). Optional;
   *  defaults to 0 so progression is correct even when the caller can't supply it. */
  gauntletsThreaded?: number;
}

function defaults(): Stats {
  return {
    gamesPlayed: 0,
    wins: 0,
    bestScore: 0,
    bestCombo: 0,
    gauntletsThreaded: 0,
    winStreak: 0,
    bestWinStreak: 0,
    dailyStreak: 0,
    bestDailyStreak: 0,
    lastDailyDate: '',
    streakFreeze: 0,
    freezeProgress: 0,
    topics: {},
    xp: 0,
    firsts: { firstWin: false, firstThread: false },
    daily: { day: '', progress: [] },
  };
}

// STREAK-FREEZE tunables (R4). One token auto-protects a SINGLE missed UTC day; tokens are earned
// one per FREEZE_EARN_EVERY kept (extended/started) days and the bank is capped at FREEZE_CAP.
const FREEZE_CAP = 2;
const FREEZE_EARN_EVERY = 7;

// =============================================================================
// PROGRESSION — the Muncher Rank ladder (XP spine) + Daily Missions.
// Pure presentation/meta over the same localStorage blob; never touches Core/determinism.
// =============================================================================

/** XP earned for a finished board: ~1 XP per 10 score points, plus a flat win bonus. */
export function boardXp(humanScore: number, won: boolean): number {
  const base = Math.max(0, Math.round(humanScore / 10));
  return base + (won ? 50 : 0);
}

/** The 6-tier Muncher Rank ladder. `at` is the cumulative lifetime-XP threshold to ENTER the tier. */
export interface RankTier {
  name: string;
  at: number;
}
export const RANKS: RankTier[] = [
  { name: 'ROOKIE', at: 0 },
  { name: 'SHARP', at: 500 },
  { name: 'PRO', at: 1500 },
  { name: 'ACE', at: 3500 },
  { name: 'SAVANT', at: 7000 },
  { name: 'MASTER', at: 12_000 },
];

export interface Progress {
  xp: number;
  /** 0-based rank index */
  rank: number;
  rankName: string;
  /** XP threshold of the NEXT tier, or the current xp when already at MASTER (capped) */
  nextAt: number;
  /** 0..1 progress from the current tier's floor to the next tier's floor (1 at max rank) */
  pctToNext: number;
  /** true when already at the top tier */
  maxed: boolean;
}

/** Resolve a lifetime-XP total into the current rank + progress-to-next (for the menu chip). */
export function getProgress(s: Stats = getStats()): Progress {
  const xp = s.xp;
  let rank = 0;
  for (let i = RANKS.length - 1; i >= 0; i -= 1) {
    if (xp >= RANKS[i].at) {
      rank = i;
      break;
    }
  }
  const maxed = rank >= RANKS.length - 1;
  const floor = RANKS[rank].at;
  const nextAt = maxed ? xp : RANKS[rank + 1].at;
  const span = maxed ? 1 : nextAt - floor;
  const pctToNext = maxed ? 1 : Math.max(0, Math.min(1, (xp - floor) / span));
  return { xp, rank, rankName: RANKS[rank].name, nextAt, pctToNext, maxed };
}

/**
 * Add bonus XP OUTSIDE the once-per-board recordBoardOutcome (e.g. a rewarded "double mission XP").
 * recordBoardOutcome is guarded once-per-board and also bumps streaks/bests/missions, so re-calling it
 * would double-count everything — this only tops up the lifetime XP pool and re-derives rank. Returns
 * the new progress (so the caller can show a rank-up). No-op for non-positive deltas.
 */
export function grantBonusXp(delta: number): Progress {
  const add = Math.max(0, Math.floor(delta));
  const s = getStats();
  if (add > 0) {
    s.xp += add;
    persist(s);
  }
  return getProgress(s);
}

// ---- Daily missions ---------------------------------------------------------

export type MissionId =
  | 'win3'
  | 'thread5'
  | 'score2500'
  | 'newTopic'
  | 'keepStreak'
  | 'combo6'
  | 'play3';

export interface MissionDef {
  id: MissionId;
  /** short menu label */
  label: string;
  /** completion target */
  target: number;
  /** XP awarded on completion */
  reward: number;
  /** how a single board contributes toward this mission (>= 0) */
  progressOf: (o: BoardOutcome, ctx: MissionCtx) => number;
}

/** Per-board context the mission `progressOf` functions need beyond the raw outcome. */
export interface MissionCtx {
  /** true if this board's topic had never been completed before THIS board */
  topicIsNew: boolean;
}

/**
 * The mission POOL (3 are chosen deterministically per UTC day). Each board contributes via
 * `progressOf`; "score2500"/"combo6" are single-board high-water marks (max), the rest accumulate.
 */
export const MISSION_POOL: MissionDef[] = [
  { id: 'win3', label: 'Win 3 boards', target: 3, reward: 120, progressOf: (o) => (o.won ? 1 : 0) },
  {
    id: 'thread5',
    label: 'Thread 5 Gauntlets',
    target: 5,
    reward: 150,
    progressOf: (o) => Math.max(0, o.gauntletsThreaded ?? 0),
  },
  {
    id: 'score2500',
    label: 'Score 2500 in one board',
    target: 2500,
    reward: 120,
    progressOf: (o) => o.humanScore, // accumulate as a single-board high-water mark (see apply)
  },
  {
    id: 'newTopic',
    label: 'Play a new topic',
    target: 1,
    reward: 80,
    progressOf: (_o, ctx) => (ctx.topicIsNew ? 1 : 0),
  },
  {
    id: 'keepStreak',
    label: 'Keep your daily streak',
    target: 1,
    reward: 100,
    progressOf: (o) => (o.isDaily ? 1 : 0),
  },
  { id: 'combo6', label: 'Hit a 6x combo', target: 6, reward: 100, progressOf: (o) => o.bestCombo },
  { id: 'play3', label: 'Play 3 boards', target: 3, reward: 80, progressOf: () => 1 },
];

/** Missions whose progress is a single-board PEAK (take the max), not a running sum. */
const PEAK_MISSIONS: ReadonlySet<MissionId> = new Set<MissionId>(['score2500', 'combo6']);

function missionDef(id: MissionId): MissionDef | undefined {
  return MISSION_POOL.find((m) => m.id === id);
}

/**
 * Deterministically pick 3 mission ids for a UTC day. A small FNV-1a hash of the day key seeds a
 * stable shuffle so the same day always yields the same 3 missions (no reroll on refresh), while
 * different days rotate. Pure function of the day string — no RNG, no Date side effects.
 */
export function missionsForDay(dayKey: string): MissionId[] {
  let h = 0x811c9dc5;
  for (let i = 0; i < dayKey.length; i += 1) {
    h ^= dayKey.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  const ids = MISSION_POOL.map((m) => m.id);
  // Fisher–Yates seeded by the day hash (LCG step), so order is stable per day.
  let seed = h || 1;
  const next = (): number => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 0x100000000;
  };
  for (let i = ids.length - 1; i > 0; i -= 1) {
    const j = Math.floor(next() * (i + 1));
    [ids[i], ids[j]] = [ids[j], ids[i]];
  }
  return ids.slice(0, 3);
}

/** Ensure `s.daily` holds today's deterministic mission set, rolling fresh on a new UTC day. */
function ensureDailyMissions(s: Stats, today = utcDayKey()): void {
  if (s.daily.day === today && s.daily.progress.length > 0) return;
  s.daily = {
    day: today,
    progress: missionsForDay(today).map((id) => ({ id, cur: 0, done: false })),
  };
}

/** A mission with its definition + live progress, for the menu mini-list. */
export interface MissionView {
  id: MissionId;
  label: string;
  target: number;
  reward: number;
  cur: number;
  done: boolean;
}

/** Today's 3 missions joined to their definitions — for the Menu's DAILY MISSIONS list. */
export function getDailyMissions(s: Stats = getStats()): MissionView[] {
  const fresh: Stats = { ...s, daily: { ...s.daily, progress: [...s.daily.progress] } };
  ensureDailyMissions(fresh);
  // Persist a fresh roll so the menu read and the next record agree on the same day's set.
  if (fresh.daily.day !== s.daily.day || fresh.daily.progress.length !== s.daily.progress.length) {
    persist(fresh);
  }
  const views: MissionView[] = [];
  for (const p of fresh.daily.progress) {
    const def = missionDef(p.id);
    if (!def) continue;
    views.push({
      id: p.id,
      label: def.label,
      target: def.target,
      reward: def.reward,
      cur: Math.min(p.cur, def.target),
      done: p.done,
    });
  }
  return views;
}

let cache: Stats | null = null;

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
function str(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

/** Read (and cache) the persisted stats. Tolerant of absent/corrupt storage. */
export function getStats(): Stats {
  if (cache) return cache;
  const d = defaults();
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) {
      cache = d;
      return cache;
    }
    const o = JSON.parse(raw) as Partial<Stats>;
    const topics: Record<string, TopicBest> = {};
    if (o.topics && typeof o.topics === 'object') {
      for (const [slug, t] of Object.entries(o.topics)) {
        if (!t || typeof t !== 'object') continue;
        const tt = t as Partial<TopicBest>;
        topics[slug] = {
          label: str(tt.label, slug),
          bestScore: num(tt.bestScore, 0),
          plays: num(tt.plays, 0),
          wins: num(tt.wins, 0),
        };
      }
    }
    // PROGRESSION hydration (tolerant of pre-progression saves: missing → defaults).
    const f = o.firsts && typeof o.firsts === 'object' ? (o.firsts as Partial<Firsts>) : {};
    const firsts: Firsts = {
      firstWin: f.firstWin === true,
      firstThread: f.firstThread === true,
    };
    let daily: DailyMissionState = { day: '', progress: [] };
    if (o.daily && typeof o.daily === 'object') {
      const od = o.daily as Partial<DailyMissionState>;
      const prog: MissionProgress[] = [];
      if (Array.isArray(od.progress)) {
        for (const p of od.progress) {
          if (!p || typeof p !== 'object') continue;
          const pp = p as Partial<MissionProgress>;
          if (typeof pp.id !== 'string') continue;
          prog.push({ id: pp.id as MissionId, cur: num(pp.cur, 0), done: pp.done === true });
        }
      }
      daily = { day: str(od.day, ''), progress: prog };
    }
    cache = {
      gamesPlayed: num(o.gamesPlayed, d.gamesPlayed),
      wins: num(o.wins, d.wins),
      bestScore: num(o.bestScore, d.bestScore),
      bestCombo: num(o.bestCombo, d.bestCombo),
      // Lifetime-gauntlets hydration (tolerant of pre-P2.5 saves: missing → 0).
      gauntletsThreaded: Math.max(0, num(o.gauntletsThreaded, d.gauntletsThreaded)),
      winStreak: num(o.winStreak, d.winStreak),
      bestWinStreak: num(o.bestWinStreak, d.bestWinStreak),
      dailyStreak: num(o.dailyStreak, d.dailyStreak),
      bestDailyStreak: num(o.bestDailyStreak, d.bestDailyStreak),
      lastDailyDate: str(o.lastDailyDate, d.lastDailyDate),
      // STREAK-FREEZE hydration (tolerant of pre-freeze saves: missing → 0, clamped to the cap).
      streakFreeze: Math.max(0, Math.min(FREEZE_CAP, num(o.streakFreeze, d.streakFreeze))),
      freezeProgress: Math.max(0, num(o.freezeProgress, d.freezeProgress)),
      topics,
      xp: num(o.xp, d.xp),
      firsts,
      daily,
    };
  } catch {
    cache = d;
  }
  return cache;
}

function persist(s: Stats): void {
  cache = s;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // private mode / blocked — stats live for the session only.
  }
}

/** YYYY-MM-DD in UTC (matches the backend's day key so daily framing agrees client/server). */
export function utcDayKey(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10);
}

/** The UTC day key for "yesterday" relative to `d` — used to decide if a daily streak continues. */
function utcYesterdayKey(d: Date = new Date()): string {
  const y = new Date(d.getTime() - 86_400_000);
  return utcDayKey(y);
}

/** The UTC day key `n` whole days before `d` — used by the streak-freeze single-day-gap check. */
function utcDaysAgoKey(n: number, d: Date = new Date()): string {
  return utcDayKey(new Date(d.getTime() - n * 86_400_000));
}

/** Win rate as a 0..100 integer percentage (0 when no games yet). */
export function winRatePct(s: Stats = getStats()): number {
  if (s.gamesPlayed <= 0) return 0;
  return Math.round((s.wins / s.gamesPlayed) * 100);
}

/** True if the Daily has already been completed for today's UTC day (drives once-a-day framing). */
export function dailyDoneToday(s: Stats = getStats()): boolean {
  return s.lastDailyDate === utcDayKey();
}

/**
 * Record a completed board into lifetime stats + streaks. Returns the new Stats AND a small
 * `delta` describing what just happened (so the Results screen can celebrate "NEW BEST!",
 * "DAILY STREAK +1", etc. without diffing the whole object itself).
 */
export interface RecordResult {
  stats: Stats;
  newBestScore: boolean;
  newBestCombo: boolean;
  /** daily streak transition: 'extended' | 'started' | 'reset' | 'none' (non-daily board) | 'repeat' */
  daily: 'extended' | 'started' | 'reset' | 'repeat' | 'none';
  /** STREAK-FREEZE (R4): true when a banked freeze was spent THIS board to protect the daily streak. */
  freezeUsed: boolean;
  /** PROGRESSION: XP earned this board (board score + win bonus + any mission rewards). */
  xpGained: number;
  /** true when this board crossed a rank threshold; `rankName` is the NEW tier. */
  rankedUp: boolean;
  /** the rank name AFTER this board (always set) */
  rankName: string;
  /** missions that completed on THIS board (for the ribbon / a one-time celebrate). */
  missionsCompleted: MissionView[];
  /** first-session achievements that fired on THIS board (firstWin / firstThread). */
  firstsUnlocked: Array<keyof Firsts>;
}

export function recordBoardOutcome(o: BoardOutcome): RecordResult {
  const prevStats = getStats();
  const prevRank = getProgress(prevStats).rank;
  const s: Stats = {
    ...prevStats,
    topics: { ...prevStats.topics },
    firsts: { ...prevStats.firsts },
    daily: { day: prevStats.daily.day, progress: prevStats.daily.progress.map((p) => ({ ...p })) },
  };

  s.gamesPlayed += 1;
  s.gauntletsThreaded += Math.max(0, Math.floor(o.gauntletsThreaded ?? 0));
  if (o.won) {
    s.wins += 1;
    s.winStreak += 1;
    s.bestWinStreak = Math.max(s.bestWinStreak, s.winStreak);
  } else {
    s.winStreak = 0;
  }

  const newBestScore = o.humanScore > s.bestScore;
  if (newBestScore) s.bestScore = o.humanScore;
  const newBestCombo = o.bestCombo > s.bestCombo;
  if (newBestCombo) s.bestCombo = o.bestCombo;

  // per-topic best — capture "new topic" BEFORE we record this play.
  const slug = o.topicNormalized || o.topicLabel.toLowerCase();
  const topicIsNew = !s.topics[slug];
  const prev = s.topics[slug] ?? { label: o.topicLabel, bestScore: 0, plays: 0, wins: 0 };
  s.topics[slug] = {
    label: o.topicLabel || prev.label,
    bestScore: Math.max(prev.bestScore, o.humanScore),
    plays: prev.plays + 1,
    wins: prev.wins + (o.won ? 1 : 0),
  };

  // daily streak — only the Daily board moves this, and only on a WIN/completion (we count any
  // completion of today's daily as "showed up"; a win isn't required to keep a habit alive).
  let daily: RecordResult['daily'] = 'none';
  let freezeUsed = false;
  if (o.isDaily) {
    const today = utcDayKey();
    if (s.lastDailyDate === today) {
      daily = 'repeat'; // already counted today — don't double-bump the streak
    } else if (s.lastDailyDate === utcYesterdayKey()) {
      s.dailyStreak += 1;
      s.lastDailyDate = today;
      daily = 'extended';
    } else if (s.lastDailyDate === '') {
      s.dailyStreak = 1;
      s.lastDailyDate = today;
      daily = 'started';
    } else if (s.lastDailyDate === utcDaysAgoKey(2) && s.streakFreeze > 0) {
      // STREAK-FREEZE (R4): the player missed exactly ONE UTC day (last daily was 2 days ago) and has
      // a token banked. Spend it to PROTECT the streak — it extends as if no day was missed instead of
      // resetting to 1. Only the single-day gap qualifies; a 2+ day gap still resets below.
      s.streakFreeze -= 1;
      s.dailyStreak += 1;
      s.lastDailyDate = today;
      freezeUsed = true;
      daily = 'extended';
    } else {
      // a gap too large to protect (or no freeze banked) — streak resets to 1 (today is the new day-1).
      s.dailyStreak = 1;
      s.lastDailyDate = today;
      daily = 'reset';
    }
    s.bestDailyStreak = Math.max(s.bestDailyStreak, s.dailyStreak);
    // Earn freezes slowly on KEPT days (a fresh extend/start, or a freeze-protected day). Each
    // FREEZE_EARN_EVERY kept days banks one token, capped at FREEZE_CAP. A 'reset' day earns nothing.
    if (daily === 'extended' || daily === 'started') {
      s.freezeProgress += 1;
      if (s.freezeProgress >= FREEZE_EARN_EVERY) {
        s.freezeProgress = 0;
        s.streakFreeze = Math.min(FREEZE_CAP, s.streakFreeze + 1);
      }
    } else if (daily === 'reset') {
      s.freezeProgress = 0;
    }
  }

  // ---- PROGRESSION: XP + missions + first-session achievements --------------
  // Base XP from this board's score (+ win bonus). Mission rewards are added on completion below.
  let xpGained = boardXp(o.humanScore, o.won);

  // Daily missions: roll today's deterministic set if needed, then advance each.
  ensureDailyMissions(s);
  const ctx: MissionCtx = { topicIsNew };
  const missionsCompleted: MissionView[] = [];
  for (const p of s.daily.progress) {
    if (p.done) {
      p.justCompleted = false;
      continue;
    }
    const def = missionDef(p.id);
    if (!def) continue;
    const add = Math.max(0, def.progressOf(o, ctx));
    p.cur = PEAK_MISSIONS.has(p.id) ? Math.max(p.cur, add) : p.cur + add;
    if (p.cur >= def.target) {
      p.done = true;
      p.justCompleted = true;
      p.cur = def.target;
      xpGained += def.reward;
      missionsCompleted.push({
        id: p.id,
        label: def.label,
        target: def.target,
        reward: def.reward,
        cur: def.target,
        done: true,
      });
    } else {
      p.justCompleted = false;
    }
  }

  // First-session achievements (each fires exactly once, ever — surfaced via the result ribbon).
  const firstsUnlocked: Array<keyof Firsts> = [];
  if (o.won && !s.firsts.firstWin) {
    s.firsts.firstWin = true;
    firstsUnlocked.push('firstWin');
  }
  if ((o.gauntletsThreaded ?? 0) > 0 && !s.firsts.firstThread) {
    s.firsts.firstThread = true;
    firstsUnlocked.push('firstThread');
  }

  s.xp += xpGained;

  const prog = getProgress(s);
  const rankedUp = prog.rank > prevRank;

  persist(s);
  return {
    stats: s,
    newBestScore,
    newBestCombo,
    daily,
    freezeUsed,
    xpGained,
    rankedUp,
    rankName: prog.rankName,
    missionsCompleted,
    firstsUnlocked,
  };
}

/** Human-readable label for a first-session achievement (for the result ribbon). */
export function firstLabel(k: keyof Firsts): string {
  switch (k) {
    case 'firstWin':
      return 'FIRST BOARD CLEARED!';
    case 'firstThread':
      return 'FIRST GAUNTLET THREADED!';
    default:
      return '';
  }
}

/**
 * If the player hasn't played the Daily for 2+ days, the live "daily streak" the menu shows should
 * read 0 (the habit is broken) even though `dailyStreak` still holds the last value until they next
 * play. This computes the *display* streak: the stored value if today/yesterday was the last daily,
 * else 0. The stored value is only mutated on the next actual daily completion (above).
 */
export function liveDailyStreak(s: Stats = getStats()): number {
  if (s.lastDailyDate === '') return 0;
  if (s.lastDailyDate === utcDayKey() || s.lastDailyDate === utcYesterdayKey()) return s.dailyStreak;
  return 0;
}

/** STREAK-FREEZE getters (R4) — for the subtle "❄ Streak Freeze: ready/used" line in the Stats panel. */
export function streakFreezeCount(s: Stats = getStats()): number {
  return Math.max(0, Math.min(FREEZE_CAP, s.streakFreeze));
}
/** Kept-days remaining until the next freeze is earned (0 when the bank is already full). */
export function freezeDaysToNext(s: Stats = getStats()): number {
  if (streakFreezeCount(s) >= FREEZE_CAP) return 0;
  return Math.max(1, FREEZE_EARN_EVERY - s.freezeProgress);
}
/** True once the player can bank no more freezes (the safety net is full). */
export function streakFreezeMaxed(s: Stats = getStats()): boolean {
  return streakFreezeCount(s) >= FREEZE_CAP;
}

/** Top per-topic bests, sorted by best score desc, for the Stats panel. */
export function topTopics(limit = 6, s: Stats = getStats()): Array<TopicBest & { slug: string }> {
  return Object.entries(s.topics)
    .map(([slug, t]) => ({ slug, ...t }))
    .sort((a, b) => b.bestScore - a.bestScore)
    .slice(0, limit);
}

/** Test/debug helper — drop the cache so a fresh read re-hydrates from storage. */
export function _resetStatsCache(): void {
  cache = null;
}
